

































































import { MonitoringAPI } from '@/app/api';
import { Pagination } from '@/app/components';
import { useAxios, useBlocks, useExecutionErrors, useFilters } from '@/app/composable';
import { WorkflowType } from '@/app/constants';
import store from '@/app/store';
import { MonitoringExecutionQuery } from '@/app/types';
import { ExecutionStatus } from '@/modules/workflow-designer/constants';
import { ExecutionHistoryEntry } from '@/modules/workflow-designer/types';
import { Ref, computed, defineComponent, ref, watch } from '@vue/composition-api';
import { SelfBuildingSquareSpinner } from 'epic-spinners';
import * as R from 'ramda';

const PAGE_SIZE = 10;

export default defineComponent({
    name: 'DataIngestionExecutions',
    components: { Pagination, SelfBuildingSquareSpinner },
    setup(props, { emit, root }) {
        const { exec, loading } = useAxios(true);
        const { formatDateAs: formatDate, fromNow } = useFilters();
        const { errorMessage } = useExecutionErrors();
        const { blocksMap } = useBlocks();

        const response: Ref<any> = ref<any>({ results: [], pagination: undefined, accessControl: undefined });
        const page = ref<number>(1);

        const user = computed(() => store.state.auth.user);

        const executions = computed((): ExecutionHistoryEntry[] => response.value?.results ?? []);
        const pagination = computed(() => response.value?.pagination);
        const rejectedItems = computed(() => response.value?.accessControl?.rejectedItems ?? 0);

        const query = computed(
            (): MonitoringExecutionQuery => ({
                query: {
                    organisationId: user.value.organisationId,
                    dateRange: { start: null, end: null },
                },
                facets: { status: [], workflowType: [], failedBlockId: [] },
                sortBy: { field: 'created_at', asc: false },
                pagination: { index: (page.value - 1) * PAGE_SIZE, pageSize: PAGE_SIZE },
            }),
        );

        const statusClasses = (status: ExecutionStatus) => {
            switch (status) {
                case ExecutionStatus.Completed:
                    return 'bg-green-200 text-green-800';
                case ExecutionStatus.Failed:
                    return 'bg-red-100 text-red-800';
                case ExecutionStatus.Running:
                    return 'bg-blue-200 text-blue-800';
                case ExecutionStatus.Queued:
                case ExecutionStatus.Pending:
                    return 'bg-gray-200 text-gray-800';
                case ExecutionStatus.Cancelled:
                    return 'bg-orange-200 text-orange-700';
                default:
                    return 'bg-neutral-200 text-neutral-800';
            }
        };

        const isFailed = (execution: ExecutionHistoryEntry) => execution.status === ExecutionStatus.Failed;

        const fileMetrics = (execution: ExecutionHistoryEntry) => {
            const metrics = execution.metrics?.[0];
            if (
                execution.metrics?.length !== 1 ||
                R.isNil(metrics?.totalFiles) ||
                R.isNil(metrics?.totalProcessedFiles) ||
                metrics.totalFiles <= 0
            )
                return undefined;
            return metrics;
        };

        const failedStepFor = (execution: ExecutionHistoryEntry) =>
            !R.isNil(execution.failedBlockId) ? blocksMap.value[`${execution.failedBlockId}`]?.name : undefined;

        const messageFor = (execution: ExecutionHistoryEntry) => {
            const metrics = fileMetrics(execution);

            if (isFailed(execution)) {
                const parts: string[] = [];
                if (execution.errors?.length) {
                    const error = errorMessage(execution.errors[0].errorCode);
                    parts.push(`${error.category} - ${error.error.title}`);
                    if (execution.errors.length > 1)
                        parts.push(
                            `and ${execution.errors.length - 1} more ${
                                execution.errors.length - 1 === 1 ? 'incident' : 'incidents'
                            }`,
                        );
                } else parts.push('The execution has failed');
                const failedStep = failedStepFor(execution);
                if (failedStep) parts.push(`failed on step '${failedStep}'`);
                return parts.join(' · ');
            }

            switch (execution.status) {
                case ExecutionStatus.Completed:
                    if (metrics && metrics.totalFiles > 1)
                        return metrics.totalFiles === metrics.totalProcessedFiles
                            ? `All ${metrics.totalFiles} files have been processed successfully`
                            : `${metrics.totalProcessedFiles} of ${metrics.totalFiles} files have been processed successfully`;
                    return 'All tasks have been executed successfully';
                case ExecutionStatus.Queued:
                    return 'The execution is in queue';
                case ExecutionStatus.Cancelled:
                    return 'The execution has been cancelled';
                case ExecutionStatus.Running:
                    return metrics
                        ? `${metrics.totalProcessedFiles} of ${metrics.totalFiles} files processed so far`
                        : 'The execution is running';
                case ExecutionStatus.Skipped:
                    return 'The execution was skipped';
                default:
                    return '';
            }
        };

        const timestampFor = (execution: ExecutionHistoryEntry) =>
            `${formatDate(execution.createdAt, true, 'DD/MM/YYYY')} at ${formatDate(
                execution.createdAt,
                true,
                'HH:mm',
                'UTC',
            )}`;

        const relativeTimestampFor = (execution: ExecutionHistoryEntry) => fromNow(execution.createdAt, true);

        const linkFor = (execution: ExecutionHistoryEntry) =>
            execution.workflow?.id
                ? {
                      name: 'data-checkin-jobs:history',
                      params: { id: `${execution.workflow.id}` },
                      query: { executionId: execution.executionId },
                  }
                : null;

        const fetchExecutions = async () => {
            await exec(MonitoringAPI.executions(query.value, WorkflowType.DataCheckin))
                .then((res: any) => {
                    response.value = res.data;
                })
                .catch((e: any) => {
                    (root as any).$toastr.e(
                        e.response?.data?.message ?? 'Ingestion executions could not be fetched',
                        'Error',
                    );
                });
        };

        const changePage = (newPage: number) => {
            page.value = newPage;
        };

        watch(
            () => query.value,
            () => fetchExecutions(),
            { deep: true, immediate: true },
        );

        watch(
            () => loading.value,
            (l: boolean) => emit('loading', l),
            { immediate: true },
        );

        return {
            executions,
            pagination,
            loading,
            rejectedItems,
            statusClasses,
            isFailed,
            messageFor,
            timestampFor,
            relativeTimestampFor,
            linkFor,
            changePage,
        };
    },
});
